import {
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FeedbackView, type FeedbackContext } from '@metro/feedback'
import { buildFeedbackStrings } from '../i18n/packageStrings'
import { FeedbackControllerContext, type FeedbackContextValue } from './useFeedback'
import { reportAnalyticsEvent } from '../analytics/webAnalyticsReporter'
import { BREAKPOINT_PX } from '../components/breakpoint'

/**
 * Host controller for the Feedback module (web port of the iOS
 * `FeedbackModule`). Owns the "which feedback context is open" state and
 * mounts a single {@link FeedbackView} over the app when a context is set.
 *
 * The two Report-an-issue entry points (RouteDetails's ReportIssueRow +
 * StationDetails's report-issue tap) call `openFeedback(context)` via
 * {@link useFeedback}; iOS presents the same module from both. Presentation
 * follows the H contract: `overlay` (full-screen sheet) below the 768px
 * breakpoint, `sidePanel` (centered form-sheet) at / above it.
 *
 * Web has no feedback backend — submission is analytics-only (mirrors
 * iOS). The `feedback_submitted` event is forwarded to the host reporter
 * sink (`reportAnalyticsEvent` → GA4 + Amplitude),
 * no-op outside production / before consent.
 */
export function FeedbackControllerProvider({ children }: { children: ReactNode }): ReactElement {
  const [context, setContext] = useState<FeedbackContext | null>(null)

  // Reactive viewport read — picks the H presentation (sidePanel ≥768px,
  // overlay below). Same matchMedia pattern the route/station surfaces use.
  const isExpanded = useSyncExternalStore(
    (cb) => {
      const mql = window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`)
      mql.addEventListener('change', cb)
      return () => mql.removeEventListener('change', cb)
    },
    () => window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`).matches,
    () => false,
  )

  const openFeedback = useCallback((ctx: FeedbackContext) => setContext(ctx), [])
  const close = useCallback(() => setContext(null), [])

  const value = useMemo<FeedbackContextValue>(() => ({ openFeedback }), [openFeedback])

  // The module is locale-agnostic and ships English; the host owns the
  // locale layer and hands it the catalog-backed set.
  const { t } = useTranslation('feedback')
  const strings = useMemo(() => buildFeedbackStrings(t), [t])

  return (
    <FeedbackControllerContext.Provider value={value}>
      {children}
      {context !== null && (
        <FeedbackView
          context={context}
          layout={isExpanded ? 'sidePanel' : 'overlay'}
          onClose={close}
          // `feedback_submitted` forwards to GA4 +
          // Amplitude in production (no-op outside production / before
          // consent).
          onAnalytics={reportAnalyticsEvent}
          strings={strings}
        />
      )}
    </FeedbackControllerContext.Provider>
  )
}
